<template>
  <MPage>
    <template #headerRight>
      <slot name="headerRight"></slot>
    </template>

    <slot name="pageHeader"></slot>

    <SearchTable ref="searchTableRef" v-bind="bindTableEvents">
      <template v-for="item in Object.keys($slots)" #[item]="data">
        <slot :name="item" v-bind="data || {}"></slot>
      </template>
    </SearchTable>

    <slot></slot>
  </MPage>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { useRoute } from 'vue-router';
  import { deepMerge, message, omit } from '@mts-fe/utils';
  import { MPage } from '@mts/components/Page';
  import SearchTable from '../components/SearchTable.vue';
  import {
    createSchemaPageContext,
    LIST_PAGE_SCHEMA,
    parsePageSchema,
    SchemaListPageProps,
    SearchTableExposed,
    TABLE_EMIT_EVENTS,
    useSchemaGlobalContext,
    useSchemaRender,
    useTableEvents,
  } from '..';

  defineOptions({
    name: 'SchemaPage',
  });

  const props = defineProps<{ schema: LIST_PAGE_SCHEMA }>();
  const emit = defineEmits(TABLE_EMIT_EVENTS);
  const bindTableEvents = useTableEvents(emit);

  const route = useRoute();
  const pageProps = ref<SchemaListPageProps>({ columns: [] });
  const searchTableRef = ref<SearchTableExposed | null>(null);

  const globalContext = useSchemaGlobalContext();
  useSchemaRender<LIST_PAGE_SCHEMA>(props, (schema) => {
    try {
      pageProps.value = parsePageSchema(schema);
      if (globalContext.schemaModifier) {
        pageProps.value = globalContext.schemaModifier(pageProps.value, {});
      }

      if (route.meta.keepSearchInfo) {
        pageProps.value.enableSearchCache = true;
      }

      const searchInfo = deepMerge(
        { ...pageProps.value.searchInfo },
        omit(route.query, ['r', 'v', 'redirect']),
      );
      searchTableRef.value?.setProps({ ...pageProps.value, searchInfo });
    } catch (error: any) {
      console.error(error);
      message.error(error.message);
    }
  });

  const pageRef = computed(() => ({ ...searchTableRef.value }));
  const modalsRef = computed(() => ({ ...(searchTableRef.value?.modalsRef || []) }));
  const pageContext = {
    pageSchema: props.schema,
    pageRef,
    pageProps,
    modalsRef,
    getBaseModalProps: props.schema['getBaseModalProps'],
  };
  // @ts-ignore
  createSchemaPageContext(pageContext);
  defineExpose(pageContext);
</script>
